import { useMemo, useState } from 'react';
import { BotIcon, CalendarPlusIcon, ChevronDownIcon, PhoneForwardedIcon, XCircleIcon } from 'lucide-react';

import { ActionAccordionCard } from '@/app/(app)/voice-agent/action-accordion-card';
import { useVoiceSettingsCtx } from '@/app/(app)/voice-agent/provider';
import { EscalationCallout } from '@/app/(app)/voice-agent/tab-routing';
import { Select } from '@/components/atoms/select';
import { ToggleButton } from '@/components/atoms/toggle-button';
import { useApiV1Ctx } from '@/components/providers/v1-api';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';

const MAX_VISIBLE_SERVICE_TYPES = 5;

export function ActionBookAppointment() {
  const {
    settings: { appointments },
    onChange: onSettingsChange,
  } = useVoiceSettingsCtx();

  const onChange = (v: Partial<(typeof appointments)['book']>) => {
    onSettingsChange({ appointments: { ...appointments, book: { ...appointments.book, ...v } } });
  };

  return (
    <ActionAccordionCard
      icon={<CalendarPlusIcon className='size-4' />}
      title='Book Appointment'
      description="Agent's ability to book appointments."
    >
      <div className='flex flex-col gap-3'>
        <Label className='px-1'>How should this action be handled?</Label>
        <div className='grid grid-cols-3 gap-2'>
          <ToggleButton
            selected={appointments.book.handling === 'ENABLED'}
            toggle={() => onChange({ handling: 'ENABLED' })}
            layout='vertical'
            className='py-4'
          >
            <BotIcon className='size-4' />
            Agent can handle
          </ToggleButton>
          <ToggleButton
            selected={appointments.book.handling === 'NOTIFY_REPRESENTATIVE'}
            toggle={() => onChange({ handling: 'NOTIFY_REPRESENTATIVE' })}
            layout='vertical'
            className='py-4'
          >
            <PhoneForwardedIcon className='size-4' />
            Notify Representative
          </ToggleButton>
          <ToggleButton
            selected={appointments.book.handling === 'DISABLED'}
            toggle={() => onChange({ handling: 'DISABLED' })}
            layout='vertical'
            className='py-4'
          >
            <XCircleIcon className='size-4' />
            Do Nothing
          </ToggleButton>
        </div>
        {appointments.book.handling === 'ENABLED' && <BookAppointmentByAgentSettings />}
        {appointments.book.handling === 'NOTIFY_REPRESENTATIVE' && (
          <div className='mt-3'>
            <EscalationCallout />
            <div className='mt-4 space-y-2'>
              <Label className='px-1'>Script</Label>
              <Textarea
                placeholder='Enter custom notification script...'
                value={appointments.book.script_notify ?? ''}
                onChange={(e) => onChange({ script_notify: e.target.value })}
                className='min-h-[100px]'
              />
            </div>
          </div>
        )}
      </div>
    </ActionAccordionCard>
  );
}

function BookAppointmentByAgentSettings() {
  const { api } = useApiV1Ctx();
  const [showAllTemplates, setShowAllTemplates] = useState(false);
  const {
    settings: { appointments },
    onChange: onSettingsChange,
  } = useVoiceSettingsCtx();

  const { data: servicePlanTemplatesData = [] } = api.servicePlanTemplates.getAllServicePlanTemplatesGet.useQuery();
  const servicePlanTemplates = useMemo(() => {
    return servicePlanTemplatesData.map((servicePlanTemplate) => {
      const handling = appointments.book.service_plan_template_rules.find(
        (rule) => rule.service_plan_template_id === servicePlanTemplate.id
      );
      return {
        id: servicePlanTemplate.id,
        name: servicePlanTemplate.name,
        handling: handling?.handling ?? 'ENABLED',
        script: handling?.script ?? '',
      };
    });
  }, [appointments.book.service_plan_template_rules, servicePlanTemplatesData]);

  const onChange = (v: Partial<typeof appointments.book>) => {
    onSettingsChange({ appointments: { ...appointments, book: { ...appointments.book, ...v } } });
  };

  const onChangePlan = (planId: string, updates: Partial<PlanHandling>) => {
    const rule = appointments.book.service_plan_template_rules.find((rule) => rule.service_plan_template_id === planId);
    if (rule) {
      onChange({
        service_plan_template_rules: [
          ...appointments.book.service_plan_template_rules.filter((rule) => rule.service_plan_template_id !== planId),
          { ...rule, handling: updates.handling ?? rule.handling, script: updates.script ?? rule.script },
        ],
      });
    } else {
      onChange({
        service_plan_template_rules: [
          ...appointments.book.service_plan_template_rules,
          {
            service_plan_template_id: planId,
            handling: updates.handling ?? 'ENABLED',
            script: updates.script ?? '',
          },
        ],
      });
    }
  };

  const propertyType = useMemo(() => {
    if (appointments.book.allowed_property_types.length > 1) {
      return 'ANY' as const;
    }
    if (appointments.book.allowed_property_types.includes('RESIDENTIAL')) {
      return 'RESIDENTIAL' as const;
    }
    if (appointments.book.allowed_property_types.includes('COMMERCIAL')) {
      return 'COMMERCIAL' as const;
    }
    return 'RESIDENTIAL' as const;
  }, [appointments.book.allowed_property_types]);

  return (
    <div className='mt-3 flex flex-col gap-6'>
      <div className='flex w-full items-center justify-between rounded-lg border bg-muted/30 p-3'>
        <Label className='px-1'>Which customers can book appointments?</Label>
        <Select
          className='w-56'
          options={[
            { label: 'Residential & Commercial', value: 'ANY' },
            { label: 'Residential', value: 'RESIDENTIAL' },
            { label: 'Commercial', value: 'COMMERCIAL' },
          ]}
          value={propertyType}
          onChange={(value) => {
            if (value === 'ANY') {
              onChange({ allowed_property_types: ['RESIDENTIAL', 'COMMERCIAL'] });
            } else {
              onChange({ allowed_property_types: [value] });
            }
          }}
        />
      </div>
      <Separator />
      <div className='flex flex-col gap-3'>
        <div className='space-y-1'>
          <Label>Subscription Handling</Label>
          <div className='text-sm text-muted-foreground'>Defined booking rules for each subscription.</div>
        </div>
        <div className='flex flex-col gap-2'>
          {(showAllTemplates ? servicePlanTemplates : servicePlanTemplates.slice(0, MAX_VISIBLE_SERVICE_TYPES)).map(
            (servicePlanTemplate) => (
              <PlanHandlingSetting
                key={servicePlanTemplate.id}
                plan={servicePlanTemplate}
                onChange={(updates) => onChangePlan(servicePlanTemplate.id, updates)}
              />
            )
          )}
          {servicePlanTemplates.length > MAX_VISIBLE_SERVICE_TYPES && (
            <Button
              variant='ghost'
              size='sm'
              onClick={() => setShowAllTemplates(!showAllTemplates)}
              className='cursor-pointer text-muted-foreground'
            >
              {showAllTemplates ? 'Show less' : `Show all (${servicePlanTemplates.length} total)`}
            </Button>
          )}
        </div>
      </div>
      <Separator />
      <ServiceNotCoveredSetting />
    </div>
  );
}

interface PlanHandling {
  id: string;
  name: string;
  handling: 'ENABLED' | 'NOTIFY_REPRESENTATIVE';
  script?: string;
}

interface PlanHandlingSetting {
  plan: PlanHandling;
  onChange: (v: Partial<PlanHandling>) => void;
}

function PlanHandlingSetting({ plan, onChange }: PlanHandlingSetting) {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleToggle = (handling: 'ENABLED' | 'NOTIFY_REPRESENTATIVE') => {
    onChange({ handling });
    setIsExpanded(true);
  };

  const handleChevronClick = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div className='rounded-lg border bg-muted/30 px-4 py-3'>
      <div className='flex items-center justify-between gap-2'>
        <div className='text-sm font-medium'>{plan.name}</div>
        <div className='flex items-center gap-2'>
          <ToggleButton
            selected={plan.handling === 'ENABLED'}
            toggle={() => handleToggle('ENABLED')}
            className='py-2 text-xs'
          >
            Agent can book
          </ToggleButton>
          <ToggleButton
            selected={plan.handling === 'NOTIFY_REPRESENTATIVE'}
            toggle={() => handleToggle('NOTIFY_REPRESENTATIVE')}
            className='py-2 text-xs'
          >
            Notify Representative
          </ToggleButton>
          <button onClick={handleChevronClick} className='rounded p-1 transition-colors hover:bg-muted'>
            <ChevronDownIcon className={`size-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
          </button>
        </div>
      </div>
      {isExpanded && (
        <div className='mt-3 border-t pt-3'>
          {plan.handling === 'ENABLED' && (
            <div className='space-y-2'>
              <Label htmlFor={`script-${plan.id}`}>Booking Script</Label>
              <Textarea
                id={`script-${plan.id}`}
                placeholder='Enter custom booking script for this subscription...'
                value={plan.script || ''}
                onChange={(e) => onChange({ script: e.target.value })}
                className='min-h-[100px]'
              />
            </div>
          )}
          {plan.handling === 'NOTIFY_REPRESENTATIVE' && <EscalationCallout />}
        </div>
      )}
    </div>
  );
}

function ServiceNotCoveredSetting() {
  const {
    settings: { appointments },
    onChange: onSettingsChange,
  } = useVoiceSettingsCtx();
  const action = appointments.book.service_not_covered_action;
  const script = appointments.book.service_not_covered_script;

  const onChange = (v: Partial<(typeof appointments)['book']>) => {
    onSettingsChange({ appointments: { ...appointments, book: { ...appointments.book, ...v } } });
  };

  return (
    <div className='flex flex-col gap-3'>
      <div className='flex items-center gap-3'>
        <div className='flex size-10 shrink-0 items-center justify-center gap-2 rounded-md bg-yellow-500/15 text-yellow-700'>
          !
        </div>
        <div className='space-y-1'>
          <Label>Service Not Covered by Subscription</Label>
          <div className='text-sm text-muted-foreground'>
            Define what happens if a customer tries to book a service or property type not covered by their subscription
            or your standard offerings.
          </div>
        </div>
      </div>
      <div className='grid grid-cols-3 gap-2'>
        <ToggleButton
          selected={action === 'END_CALL'}
          toggle={() => onChange({ service_not_covered_action: 'END_CALL' })}
          layout='vertical'
        >
          <XCircleIcon className='size-4' />
          End Call
        </ToggleButton>
        <ToggleButton
          selected={action === 'NOTIFY_REPRESENTATIVE'}
          toggle={() => onChange({ service_not_covered_action: 'NOTIFY_REPRESENTATIVE' })}
          layout='vertical'
        >
          <PhoneForwardedIcon className='size-4' />
          Notify Representative
        </ToggleButton>
        <ToggleButton
          selected={action === 'STILL_BOOK'}
          toggle={() => onChange({ service_not_covered_action: 'STILL_BOOK' })}
          layout='vertical'
        >
          <CalendarPlusIcon className='size-4' />
          Still Book
        </ToggleButton>
      </div>
      {action === 'END_CALL' && (
        <div className='mt-1 space-y-1.5'>
          <Label>Script</Label>
          <Textarea
            placeholder='Enter custom script for this action...'
            value={script || ''}
            onChange={(e) => onChange({ service_not_covered_script: e.target.value })}
            className='min-h-[100px]'
          />
        </div>
      )}
      {action === 'NOTIFY_REPRESENTATIVE' && <EscalationCallout />}
      {action === 'STILL_BOOK' && (
        <div className='mt-1 px-2 text-sm text-muted-foreground'>
          The agent will attempt to book the appointment. Consider internal workflows for reviewing such bookings.
        </div>
      )}
    </div>
  );
}
